import React, { useState, useEffect } from "react";
import cloneDeep from "lodash/cloneDeep";
import Table from "@cloudscape-design/components/table";
import Pagination from "@cloudscape-design/components/pagination";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Grid from "@cloudscape-design/components/grid";
import Badge from "@cloudscape-design/components/badge";
import Preferences from "@cloudscape-design/components/collection-preferences";
import Alert from "@cloudscape-design/components/alert";
import Select from "@cloudscape-design/components/select";
import Modal from "@cloudscape-design/components/modal";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Tabs from "@cloudscape-design/components/tabs";
import DatePicker from "@cloudscape-design/components/date-picker";
import Spinner from "@cloudscape-design/components/spinner";
import Checkbox from "@cloudscape-design/components/checkbox";
import { Container } from "react-bootstrap";
/* Recoil */
import { useRecoilValue, useSetRecoilState } from "recoil";
import { configStore } from "../stores/config";
import { playerModalStore } from "../stores/playerModal";
import { bonusesStore } from "../stores/bonuses";
import { bonusesAddModalStore } from "../stores/bonusesAddModal";
import { bonusesEditModalStore } from "../stores/bonusesEditModal";


const hasAffiliateValue = (value) =>
	value !== null &&
	value !== undefined &&
	!(typeof value === "string" && value.trim() === "");

const isAffiliatePromo = (item) => Number(item.type) === 1;

const formatAffiliateCurrency = (value) => {
	if (!hasAffiliateValue(value)) {
		return "No";
	}

	const numericValue = Number(value);
	if (Number.isNaN(numericValue)) {
		return value;
	}

	return `${new Intl.NumberFormat("ru-RU").format(numericValue)} RUB.`;
};

const parseAdPayments = (rawValue) => {
	if (!rawValue) {
		return [];
	}

	let parsedValue = rawValue;
	if (typeof parsedValue === "string") {
		try {
			parsedValue = JSON.parse(parsedValue);
		} catch (error) {
			return [];
		}
	}

	if (!Array.isArray(parsedValue)) {
		if (parsedValue && typeof parsedValue === "object") {
			parsedValue = [parsedValue];
		} else {
			return [];
		}
	}

	return parsedValue
		.map((payment) => ({
			date: payment?.date || "",
			amount: payment?.amount ?? "",
		}))
		.filter((payment) => hasAffiliateValue(payment.date) || hasAffiliateValue(payment.amount));
};

const getAffiliateModalState = (item) => ({
	trafficType: item.traffic_type || "",
	trafficSource: item.traffic_source || "",
	adCostTotal: item.ad_cost_total ?? "",
	adPayments: parseAdPayments(item.ad_payments_json),
});

const renderAffiliatePaymentsCell = (item) => {
	if (!isAffiliatePromo(item)) {
		return "";
	}

	const adPayments = parseAdPayments(item.ad_payments_json);
	if (!adPayments.length) {
		return "No";
	}

	return (
		<SpaceBetween direction="vertical" size="xs">
			{adPayments.map((payment, index) => {
				const dateLabel = hasAffiliateValue(payment.date) ? payment.date : "No date";
				const amountLabel = formatAffiliateCurrency(payment.amount);

				return (
					<Box key={`ad-payment-${item.id}-${index}`}>
						{`${dateLabel} - ${amountLabel}`}
					</Box>
				);
			})}
		</SpaceBetween>
	);
};

const initialColumnDefinitions = [
	{
		id: "id",
		header: "ID",
		sortingField: "id",
		minWidth: 160,
		cell: (item, { setBonusesEditModal, bonusesEditModal, config }) => (
			<Button
				iconName={"status-info"}
				variant="link"
				onClick={() => {
					setTimeout(() => {
						setBonusesEditModal({
							...bonusesEditModal,
							id: item.id,
							editAll: false,
							count: item.count,
							value: item.value,
							advancedSettingsEnabled: item.reason ? true : false,
							type: config.bonuses.options.type.filter(
								(element) => element.type === item.type
							)[0],
							isPublic: item.type === 0,
							bonusCode: item.bonus_code ? item.bonus_code : "",
							wager: item.wager ? { value: item.wager, label: `x${item.wager}` } : null,
							maxWithdrawal: item.max_withdrawal ? item.max_withdrawal / 100 : "",
							depositTo: item.deposit_amount_to ? item.deposit_amount_to / 100 : "",
							depositFrom: item.deposit_amount_from ? item.deposit_amount_from / 100 : "",
							depositCountDays: typeof item.deposit_days === 'number' ? item.deposit_days : "",
							depositCountTo: typeof item.deposit_count_to === 'number' ? item.deposit_count_to : "",
							depositCount: typeof item.deposit_count === 'number' ? item.deposit_count : "",
							emailChecked: item.require_email ? item.require_email : false,
							phoneChecked: item.require_phone ? item.require_phone : false,
							tgChecked: item.require_tg ? item.require_tg : false,
							verifiedChecked: item.require_verification ? item.require_verification : false,
							lastDepositDateFrom:
								item.deposit_period_from && item.deposit_period_from !== "0000-00-00 00:00:00"
									? item.deposit_period_from?.replace(" ", "T")
									: null,
							lastDepositDateTo:
								item.deposit_period_to && item.deposit_period_to !== "0000-00-00 00:00:00"
									? item.deposit_period_to?.replace(" ", "T")
									: null,
							otherPromoActivated: item.require_other_promo ? true : false,
							otherPromoAmount: item.restrict_other_promo_amount ? item.restrict_other_promo_amount / 100 : "",
							otherPromoDateFrom:
								item.restrict_other_promo_date_from &&
								item.restrict_other_promo_date_from !== "0000-00-00 00:00:00"
									? item.restrict_other_promo_date_from?.replace("_", "T")
									: null,
							otherPromoDateTo:
								item.restrict_other_promo_date_to && item.restrict_other_promo_date_to !== "0000-00-00 00:00:00"
									? item.restrict_other_promo_date_to?.replace("_", "T")
									: null,
							activation_lifetime_days:
								typeof item.activation_lifetime_days === 'number'
									? item.activation_lifetime_days
									: "N/A",
							considerBetAmount: item.require_bet ? true : false,
							betAmount: item.restrict_bets_amount ? item.restrict_bets_amount / 100 : "",
							betDateFrom:
								item.restrict_bets_date_from && item.restrict_bets_date_from !== "0000-00-00 00:00:00"
									? item.restrict_bets_date_from?.replace("_", "T")
									: null,
							betDateTo:
								item.restrict_bets_date_to && item.restrict_bets_date_to !== "0000-00-00 00:00:00"
									? item.restrict_bets_date_to?.replace("_", "T")
									: null,
							vipLevelMode: item.vip_level_type ? { value: item.vip_level_type, label: item.vip_level_type } : "",
							vipLevel: item.vip_level_value ? item.vip_level_value : "",
							promoDateFrom:
								item.from && item.from !== "0000-00-00 00:00:00"
									? item.from?.replace("_", "T")
									: null,
							promoDateTo:
								item.to && item.to !== "0000-00-00 00:00:00"
									? item.to?.replace("_", "T")
									: null,
							reason: item.reason ? { value: item.reason, label: item.reason } : "",
							note: item.note,
							bonusMode: typeof item.bonusMode === 'number' ? item.bonusMode : Number(item.bonusMode) || 0,
							depositPercent: typeof item.depositPercent === 'number' ? item.depositPercent : item.depositPercent || "",
							depositApplyLimit: typeof item.depositApplyLimit === 'number' ? item.depositApplyLimit : item.depositApplyLimit || "",
							minDeposit: typeof item.minDeposit === 'number'
								? item.minDeposit / 100
								: item.minDeposit ? Number(item.minDeposit) / 100 : "",
							maxDeposit: typeof item.maxDeposit === 'number'
								? item.maxDeposit / 100
								: item.maxDeposit ? Number(item.maxDeposit) / 100 : "",
							maxBonusAmount: typeof item.max_bonus_amount === 'number' ? item.max_bonus_amount / 100 : item.max_bonus_amount || "",
							bonusType: item.bonus_type
								? { value: item.bonus_type, label: item.bonus_type.charAt(0).toUpperCase() + item.bonus_type.slice(1) }
								: { value: 'all', label: 'All' },
							bonusAmount: item.value,
							name: item.code,
							uid: item.uid,
							...getAffiliateModalState(item),
							show: true,
						});
					}, 0);
				}}
		//		disabled={item.isDisabled}
				children={item.id}
			/>
		),
	},
	{ id: "code", header: "Name", sortingField: "code", cell: (item) => item.code },
	{
		id: "uid",
		header: "UID",
		sortingField: "uid",
		cell: (item, { handleUIDClick, styles }) =>
			item.uid ? (
				<Button
					iconName={"status-in-progress"}
					className={[styles["btnUID"], styles["blue"]].join(" ")}
					variant="normal"
					onClick={(ev) => handleUIDClick(ev, item.uid)}
					children={item.uid}
				/>
			) : (
				"none"
			),
	},
	{ id: "reason", header: "Reason", sortingField: "reason", cell: (item) => (item.reason ? item.reason : "No") },
	{ id: "note", header: "Note", sortingField: "note", cell: (item) => item.note },
	{
		id: "value",
		header: "Value",
		sortingField: "value",
		cell: (item) =>
			Number(item.bonusMode) === 1
				? "N/A"
				: <>{new Intl.NumberFormat("ru-RU").format(item.value)} RUB.</>,
	},
	{
		id: "bonusMode",
		header: "Mode",
		sortingField: "bonusMode",
		cell: (item) => (Number(item.bonusMode) === 1 ? "Deposit %" : "Fixed"),
	},
	{
		id: "depositPercent",
		header: "Deposit % / Deposits",
		sortingField: "depositPercent",
		cell: (item) => {
			if (Number(item.bonusMode) !== 1) {
				return "N/A";
			}

			const percent = item.depositPercent;
			const applyLimit = item.depositApplyLimit;
			const percentLabel =
				percent || percent === 0 ? `${percent}%` : "N/A";
			const limitLabel =
				applyLimit || applyLimit === 0 ? `${applyLimit} deposits` : "N/A";

			return `${percentLabel} / ${limitLabel}`;
		},
	},
	{
		id: "max_activation",
		minWidth: 220,
		header: "Maximum activation",
		sortingField: "max_activation",
		cell: (item) => (item.max_activation ? item.max_activation : "No"),
	},
	{ id: "count", minWidth: 150, header: "Activation", sortingField: "count", cell: (item) => item.count },
	{
		id: "type",
		header: "Type",
		sortingField: "type",
		cell: (item, { styles, getTypeColor, getTypeByCode }) => (
			<Badge
				className={[styles["badge"], styles[getTypeColor(item.type)]].join(" ")}
			>
				{getTypeByCode(item.type)}
			</Badge>
		),
	},
	{
		id: "traffic_type",
		header: "Traffic type",
		sortingField: "traffic_type",
		minWidth: 180,
		cell: (item) => {
			if (!isAffiliatePromo(item)) {
				return "";
			}

			return hasAffiliateValue(item.traffic_type) ? item.traffic_type : "No";
		},
	},
	{
		id: "traffic_source",
		header: "Traffic source",
		sortingField: "traffic_source",
		minWidth: 220,
		cell: (item) => {
			if (!isAffiliatePromo(item)) {
				return "";
			}

			return hasAffiliateValue(item.traffic_source) ? item.traffic_source : "No";
		},
	},
	{
		id: "ad_cost_total",
		header: "Total ad cost",
		sortingField: "ad_cost_total",
		minWidth: 180,
		cell: (item) => {
			if (!isAffiliatePromo(item)) {
				return "";
			}

			return formatAffiliateCurrency(item.ad_cost_total);
		},
	},
	{
		id: "ad_payments_json",
		header: "Ad payments",
		minWidth: 280,
		cell: (item) => renderAffiliatePaymentsCell(item),
	},
	{
		id: "bonus_code",
		minWidth: 150,
		header: "Bonus code",
		sortingField: "bonus_code",
		cell: (item) => (item.bonus_code ? item.bonus_code : "No"),
	},
	{
		id: "deposit_count",
		minWidth: 220,
		header: "Deposit count",
		sortingField: "deposit_count",
		cell: (item) => (item.deposit_count ? item.deposit_count : "No"),
	},
	{
		id: "deposit_amount_from",
		header: "Deposit from",
		minWidth: 220,
		sortingField: "deposit_amount_from",
		cell: (item) =>
			item.deposit_amount_from ? (
				<>{new Intl.NumberFormat("ru-RU").format(item.deposit_amount_from / 100)} RUB.</>
			) : (
				"No"
			),
	},
	{
		id: "deposit_amount_to",
		header: "Deposit to",
		minWidth: 220,
		sortingField: "deposit_amount_to",
		cell: (item) =>
			item.deposit_amount_to ? (
				<>{new Intl.NumberFormat("ru-RU").format(item.deposit_amount_to / 100)} RUB.</>
			) : (
				"No"
			),
	},
	{
		id: "deposit_period_from",
		header: "Deposit period from",
		minWidth: 220,
		sortingField: "deposit_period_from",
		cell: (item) =>
			item.deposit_period_from && item.deposit_period_from !== "0000-00-00 00:00:00" ? (
				<>
					{new Date(item.deposit_period_from).toLocaleDateString() +
						" " +
						new Date(item.deposit_period_from).toLocaleTimeString()}
				</>
			) : (
				"No"
			),
	},
	{
		id: "deposit_period_to",
		header: "Deposit period to",
		minWidth: 220,
		sortingField: "deposit_period_to",
		cell: (item) =>
			item.deposit_period_to && item.deposit_period_to !== "0000-00-00 00:00:00" ? (
				<>
					{new Date(item.deposit_period_to).toLocaleDateString() +
						" " +
						new Date(item.deposit_period_to).toLocaleTimeString()}
				</>
			) : (
				"No"
			),
	},
	{
		id: "max_withdrawal",
		minWidth: 220,
		header: "Maximum withdrawal",
		sortingField: "max_withdrawal",
		cell: (item) =>
			item.max_withdrawal ? (
				<>{new Intl.NumberFormat("ru-RU").format(item.max_withdrawal / 100)} RUB.</>
			) : (
				"No"
			),
	},
	{
		id: "require_email",
		minWidth: 200,
		header: "Require Email",
		sortingField: "require_email",
		cell: (item) => (item.require_email ? item.require_email : "No"),
	},
	{
		id: "require_phone",
		minWidth: 200,
		header: "Require Phone",
		sortingField: "require_phone",
		cell: (item) => (item.require_phone ? item.require_phone : "No"),
	},
	{
		id: "require_tg",
		minWidth: 200,
		header: "Require tg",
		sortingField: "require_tg",
		cell: (item) => (item.require_tg ? item.require_tg : "No"),
	},
	{
		id: "require_verification",
		header: "Require verification",
		minWidth: 220,
		sortingField: "require_verification",
		cell: (item) => (item.require_verification ? item.require_verification : "No"),
	},
	{
		id: "restrict_bets_amount",
		header: "Bets amount",
		minWidth: 200,
		sortingField: "restrict_bets_amount",
		cell: (item) =>
			item.restrict_bets_amount ? (
				<>{new Intl.NumberFormat("ru-RU").format(item.restrict_bets_amount / 100)} RUB.</>
			) : (
				"No"
			),
	},
	{
		id: "restrict_bets_date_from",
		header: "Bets date from",
		minWidth: 220,
		sortingField: "restrict_bets_date_from",
		cell: (item) =>
			item.restrict_bets_date_from && item.restrict_bets_date_from !== "0000-00-00 00:00:00" ? (
				<>
					{new Date(item.restrict_bets_date_from).toLocaleDateString() +
						" " +
						new Date(item.restrict_bets_date_from).toLocaleTimeString()}
				</>
			) : (
				"No"
			),
	},
	{
		id: "restrict_bets_date_to",
		header: "Bets date to",
		minWidth: 220,
		sortingField: "restrict_bets_date_to",
		cell: (item) =>
			item.restrict_bets_date_to && item.restrict_bets_date_to !== "0000-00-00 00:00:00" ? (
				<>
					{new Date(item.restrict_bets_date_to).toLocaleDateString() +
						" " +
						new Date(item.restrict_bets_date_to).toLocaleTimeString()}
				</>
			) : (
				"No"
			),
	},
	{
		id: "restrict_other_promo_amount",
		header: "Other promo amount",
		sortingField: "restrict_other_promo_amount",
		cell: (item) =>
			item.restrict_other_promo_amount ? (
				<>{new Intl.NumberFormat("ru-RU").format(item.restrict_other_promo_amount / 100)} RUB.</>
			) : (
				"No"
			),
	},
	{
		id: "restrict_other_promo_date_from",
		header: "Other promo datefrom",
		sortingField: "restrict_other_promo_date_from",
		cell: (item) =>
			item.restrict_other_promo_date_from && item.restrict_other_promo_date_from !== "0000-00-00 00:00:00" ? (
				<>
					{new Date(item.restrict_other_promo_date_from).toLocaleDateString() +
						" " +
						new Date(item.restrict_other_promo_date_from).toLocaleTimeString()}
				</>
			) : (
				"No"
			),
	},
	{
		id: "restrict_other_promo_date_to",
		header: "Other promo date to",
		sortingField: "restrict_other_promo_date_to",
		cell: (item) =>
			item.restrict_other_promo_date_to && item.restrict_other_promo_date_to !== "0000-00-00 00:00:00" ? (
				<>
					{new Date(item.restrict_other_promo_date_to).toLocaleDateString() +
						" " +
						new Date(item.restrict_other_promo_date_to).toLocaleTimeString()}
				</>
			) : (
				"No"
			),
	},
	{
		id: "from",
		header: "Validity period from",
		minWidth: 220,
		sortingField: "from",
		cell: (item) =>
			item.from && item.from !== "0000-00-00 00:00:00" ? (
				<>
					{new Date(item.from).toLocaleDateString() +
						" " +
						new Date(item.from).toLocaleTimeString()}
				</>
			) : (
				"No"
			),
	},
	{
		id: "to",
		header: "Validity period to",
		minWidth: 220,
		sortingField: "to",
		cell: (item) =>
			item.to && item.to !== "0000-00-00 00:00:00" ? (
				<>
					{new Date(item.to).toLocaleDateString() +
						" " +
						new Date(item.to).toLocaleTimeString()}
				</>
			) : (
				"No"
			),
	},
	{
		id: "vip_level_type",
		header: "Vip lvl type",
		sortingField: "vip_level_type",
		cell: (item) => (item.vip_level_type ? item.vip_level_type : "No"),
	},
	{
		id: "vip_level_value",
		header: "Vip lvl",
		sortingField: "vip_level_value",
		cell: (item) => (item.vip_level_value ? item.vip_level_value : "No"),
	},
	{ id: "wager", header: "Wager", sortingField: "wager", cell: (item) => (item.wager ? item.wager : "No") },
	{
		id: "date",
		header: "Date",
		sortingField: "date",
		cell: (item) => (
			<>
				{new Date(item.date).toLocaleDateString() + " " + new Date(item.date).toLocaleTimeString()}
			</>
		),
	},
	{
		id: "action",
		header: "Action",
		cell: (item, { setBonusesAddModal, bonusesAddModal, config, styles }) => (
			<Button
				disabled={!bonusesAddModal.loaded}
				className={[styles["ButtonTextNoWrap"]].join(" ")}
				onClick={() => {
					setTimeout(() => {
						setBonusesAddModal({
							...bonusesAddModal,
							id: item.id,
							count: item.count,
							advancedSettingsEnabled: item.reason ? true : false,
							value: item.value,
							type: config.bonuses.options.type.filter(
								(element) => element.type === item.type
							)[0],
							isPublic: item.type === 0,
							bonusCode: item.bonus_code ? item.bonus_code : "",
							wager: item.wager ? { value: item.wager, label: `x${item.wager}` } : null,
							maxWithdrawal: item.max_withdrawal ? item.max_withdrawal / 100 : "",
							depositTo: item.deposit_amount_to ? item.deposit_amount_to / 100 : "",
							depositFrom: item.deposit_amount_from ? item.deposit_amount_from / 100 : "",
							depositCount: typeof item.deposit_count === 'number' ? item.deposit_count : "",
							depositCountDays: typeof item.deposit_days === 'number' ? item.deposit_days : "",
							depositCountTo: typeof item.deposit_count_to === 'number' ? item.deposit_count_to : "",
							emailChecked: item.require_email ? item.require_email : false,
							phoneChecked: item.require_phone ? item.require_phone : false,
							tgChecked: item.require_tg ? item.require_tg : false,
							verifiedChecked: item.require_verification ? item.require_verification : false,
							lastDepositDateFrom:
								item.deposit_period_from && item.deposit_period_from !== "0000-00-00 00:00:00"
									? item.deposit_period_from?.replace(" ", "T")
									: null,
							lastDepositDateTo:
								item.deposit_period_to && item.deposit_period_to !== "0000-00-00 00:00:00"
									? item.deposit_period_to?.replace(" ", "T")
									: null,
							otherPromoActivated: item.require_other_promo ? true : false,
							otherPromoAmount: item.restrict_other_promo_amount ? item.restrict_other_promo_amount / 100 : "",
							otherPromoDateFrom:
								item.restrict_other_promo_date_from &&
								item.restrict_other_promo_date_from !== "0000-00-00 00:00:00"
									? item.restrict_other_promo_date_from?.replace("_", "T")
									: null,
							otherPromoDateTo:
								item.restrict_other_promo_date_to && item.restrict_other_promo_date_to !== "0000-00-00 00:00:00"
									? item.restrict_other_promo_date_to?.replace("_", "T")
									: null,
							activation_lifetime_days:
								typeof item.activationLifeTimeDays === 'number'
									? item.activationLifeTimeDays
									: typeof item.activation_lifetime_days === 'number'
										? item.activation_lifetime_days
										: item.activationLifeTimeDays || item.activation_lifetime_days || "",
							considerBetAmount: item.restrict_bets_amount ? true : false,
							betAmount: item.restrict_bets_amount ? item.restrict_bets_amount / 100 : "",
							betDateFrom:
								item.restrict_bets_date_from && item.restrict_bets_date_from !== "0000-00-00 00:00:00"
									? item.restrict_bets_date_from?.replace("_", "T")
									: null,
							betDateTo:
								item.restrict_bets_date_to && item.restrict_bets_date_to !== "0000-00-00 00:00:00"
									? item.restrict_bets_date_to?.replace("_", "T")
									: null,
							vipLevelMode: item.vip_level_type ? { value: item.vip_level_type, label: item.vip_level_type } : "",
							vipLevel: item.vip_level_value ? item.vip_level_value : "",
							promoDateFrom:
								item.from && item.from !== "0000-00-00 00:00:00"
									? item.from?.replace("_", "T")
									: null,
							promoDateTo:
								item.to && item.to !== "0000-00-00 00:00:00"
									? item.to?.replace("_", "T")
									: null,
							reason: item.reason ? { value: item.reason, label: item.reason } : "",
							note: item.note,
							bonusMode: typeof item.bonusMode === 'number' ? item.bonusMode : Number(item.bonusMode) || 0,
							depositPercent: typeof item.depositPercent === 'number' ? item.depositPercent : item.depositPercent || "",
							depositApplyLimit: typeof item.depositApplyLimit === 'number' ? item.depositApplyLimit : item.depositApplyLimit || "",
							minDeposit: typeof item.minDeposit === 'number'
								? item.minDeposit / 100
								: item.minDeposit ? Number(item.minDeposit) / 100 : "",
							maxDeposit: typeof item.maxDeposit === 'number'
								? item.maxDeposit / 100
								: item.maxDeposit ? Number(item.maxDeposit) / 100 : "",
							maxBonusAmount: typeof item.max_bonus_amount === 'number' ? item.max_bonus_amount / 100 : item.max_bonus_amount || "",
							bonusType: item.bonus_type
									? { value: item.bonus_type, label: item.bonus_type.charAt(0).toUpperCase() + item.bonus_type.slice(1) }
									: { value: 'all', label: 'All' },
							bonusAmount: item.value,
							name: item.code,
							uid: item.uid !== 0 ? item.uid : null,
							...getAffiliateModalState(item),
							show: true,
						});
					}, 0);
				}}
				variant="primary"
				children="Duplicate"
			/>
		),
	},
];

const getDefaultColumnVisibility = () => {
	const initialVisibility = {};
	initialColumnDefinitions.forEach((col) => {
		initialVisibility[col.id] = true;
	});

	return initialVisibility;
};

const Bonuses = ({
									 Interface,
									 bonuses,
									 setBonuses,
									 config,
									 setConfig,
									 playerModal,
									 setPlayerModal,
									 bonusesAddModal,
									 setBonusesAddModal,
									 bonusesEditModal,
									 setBonusesEditModal,
									 styles,
								 }) => {
	const [showDeactivateModal, setShowDeactivateModal] = useState(false);
	const [isExporting, setIsExporting] = useState(false);
	const [activeTabId, setActiveTabId] = useState("promoCodes");
	const [statisticsStartDate, setStatisticsStartDate] = useState("");
	const [statisticsEndDate, setStatisticsEndDate] = useState("");
	const [statisticsData, setStatisticsData] = useState([]);
	const [statisticsLoading, setStatisticsLoading] = useState(false);
	const [statisticsError, setStatisticsError] = useState(null);
	const [statisticsPeriod, setStatisticsPeriod] = useState("day");
	const [loadedDeactivated, setLoadedDeactivated] = useState(false);
	const [deleteAllPromo, setDeleteAllPromo] = useState(false);

	const [columnVisibility, setColumnVisibility] = useState(() => {
		const initialVisibility = getDefaultColumnVisibility();
		const saved = localStorage.getItem("bonuses.columnVisibility");
		if (saved) {
			try {
				return {
					...initialVisibility,
					...JSON.parse(saved),
				};
			} catch (error) {
				return initialVisibility;
			}
		}
		return initialVisibility;
	});

	useEffect(() => {
		Interface.loadBonuses();
	}, []);

	const handleInputDebounceChange = (input) => {
		setBonuses({
			...bonuses,
			currentSearch: input.detail.value,
			pagination: {
				...bonuses.pagination,
				current: 1,
			},
		});

		if (input.detail.value.length === 0) {
			setTimeout(() => Interface.loadBonuses(), 250);
		}
	};

	const compare = (a, b) => {
		if (
			a[bonuses.sorting.sortingColumn.sortingField] <
			b[bonuses.sorting.sortingColumn.sortingField]
		) {
			return bonuses.sorting.isDescending ? 1 : -1;
		}

		if (
			a[bonuses.sorting.sortingColumn.sortingField] >
			b[bonuses.sorting.sortingColumn.sortingField]
		) {
			return bonuses.sorting.isDescending ? -1 : 1;
		}

		return 0;
	};

	const handleKeyPress = (event) => {
		if (event.detail.key === "Enter") {
			setTimeout(() => {
				Interface.loadBonuses();
			}, 0);
		}
	};

	const getTypeColor = (type) => {
		switch (type) {
			case 1:
				return "purple";
			case 0:
				return "blue";
			case 2:
				return "green";
			default:
				return "black";
		}
	};

	const getTypeByCode = (type) => {
		switch (type) {
			case 1:
				return "Affiliative";
			case 0:
				return "Public";
			case 2:
				return "Dedicated";
			default:
				return "Unknown";
		}
	};

	const handleUIDClick = (event, uid) => {
		if (!event.detail.shiftKey) {
			Interface.loadInfo(uid);
		} else {
			let hasProperty = false,
				selectedItemsCurrent = cloneDeep(bonuses.selectedItems);

			selectedItemsCurrent.forEach((item) => {
				if (item.uid === uid) hasProperty = true;
			});

			let j = 0,
				withdrawals = cloneDeep(bonuses.data).slice(
					(bonuses.pagination.current - 1) * config.withdrawals.perPage,
					bonuses.pagination.current * config.withdrawals.perPage
				),
				selectedItems = cloneDeep(bonuses.selectedItems);

			withdrawals.forEach((item) => {
				if (item.uid === uid) {
					if (hasProperty) {
						selectedItems.forEach((itemSelected, i) => {
							if (itemSelected.id === item.id) {
								selectedItems.splice(i, 1);
								j++;
							}
						});
					} else {
						selectedItems.push(item);
						j++;
					}
				}
			});

			setBonuses({ ...bonuses, selectedItems });
		}
	};

	const handleOpenDeactivateModal = () => {
		setDeleteAllPromo(false);
		setShowDeactivateModal(true);
	};

	const handleCloseDeactivateModal = () => {
		setDeleteAllPromo(false);
		setShowDeactivateModal(false);
	};

	const handleConfirmDeactivate = async () => {
		try {
			setLoadedDeactivated(true);
			const selectedIds = bonuses.selectedItems.map((item) => item.id);
			await Interface.request("set", { class: "data", module: "bonuses", method: "edit" }, {
				id: selectedIds,
				count: 0,
				type: 1,
				isDelete: Boolean(deleteAllPromo),
			});
			setBonuses({
				...bonuses,
				selectedItems: [],
			});
			setTimeout(() => {
				Interface.loadBonuses();
			}, 0);
			setDeleteAllPromo(false);
			setShowDeactivateModal(false);
		} catch (error) {
			console.error("Failed to deactivate bonuses:", error);
		} finally {
			setLoadedDeactivated(false);
		}
	};

	const handleExportToCsv = () => {
		setIsExporting(true);
		const { currentSearch, currentSearchType, sorting } = bonuses;
		Interface.request("get", { class: "data", module: "bonuses", method: "exportToCsv" }, {
			search: currentSearch,
			searchType: currentSearchType,
			sorting: {
				column: sorting.sortingColumn.column,
				order: sorting.sortingColumn.order,
			},
		})
			.then((response) => {
				const blob = new Blob([response.csv], { type: "text/csv;charset=utf-8;" });
				const link = document.createElement("a");
				link.href = URL.createObjectURL(blob);
				link.download = response.filename;
				link.click();
				URL.revokeObjectURL(link.href);
				setIsExporting(false);
			})
			.catch((error) => {
				console.error("Failed to export CSV:", error);
				setIsExporting(false);
				setBonuses({
					...bonuses,
					error: {
						text: "Failed to export promo codes to CSV",
						code: error.code || 500,
						timestamp: new Date().toISOString(),
					},
				});
			});
	};

	const loadStatistics = () => {
		if (!statisticsStartDate || !statisticsEndDate) {
			setStatisticsError("Please select start and end dates");
			return;
		}

		setStatisticsLoading(true);
		setStatisticsError(null);

		Interface.request("get", { class: "data", module: "bonuses", method: "statistics" }, {
			start_date: statisticsStartDate,
			end_date: statisticsEndDate,
			period: statisticsPeriod,
		})
			.then((response) => {
				const data = response.data.map((item) => ({
					date: item.period,
					totalIssued: item.total_issued || 0,
					totalActivated: item.total_activated || 0,
					activationPercentage: ((item.total_activated  / item.total_issued) * 100).toFixed(2) || 0,
				}));
				setStatisticsData(data);
				setStatisticsLoading(false);
			})
			.catch((error) => {
				console.error("Failed to load statistics:", error);
				setStatisticsLoading(false);
				setStatisticsError("Failed to load statistics");
			});
	};

	const handleDateChange = (key) => (event) => {
		if (key === "statisticsStartDate") {
			setStatisticsStartDate(event.detail.value);
		} else {
			setStatisticsEndDate(event.detail.value);
		}
		setStatisticsData([]);
		setStatisticsError(null);
	};

	const handleTabChange = (event) => {
		setActiveTabId(event.detail.activeTabId);
	};


	const filteredColumnDefinitions = initialColumnDefinitions.filter(
		(col) => columnVisibility[col.id]
	);


	const visibleContentOptions = initialColumnDefinitions.map((col) => ({
		id: col.id,
		label: col.header,
	}));

	const startDate = statisticsStartDate
		? new Date(statisticsStartDate).toLocaleDateString("en-US", {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
		})
		: "";
	const endDate = statisticsEndDate
		? new Date(statisticsEndDate).toLocaleDateString("en-US", {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
		})
		: "";
	const currentDateIndex = statisticsData.findIndex(
		(item) => item.date === new Date().toISOString().split("T")[0]
	);

	const dateColumns = statisticsData.map((item, index) => ({
		id: `date-${index}`,
		header: (
			<Box
				textAlign="center"
				padding={{ vertical: "xxs" }}
				backgroundColor={index === currentDateIndex ? "#ebedf0" : "transparent"}
			>
				{item.date}
			</Box>
		),
		cell: (item) => item[`value-${index}`] || "0",
	}));

	return (
		<>
			{bonuses.loaded && bonuses.error.text.length ? (
				<Box
					margin={{ bottom: "m" }}
					children={
						<Alert
							type="error"
							header="Error loading data:"
							children={
								<>
									<Box variant="p" children={bonuses.error.text} />
									<hr />
									<Box
										variant="p"
										children={
											<>
												request error code: {bonuses.error.code} | request timestamp: {bonuses.error.timestamp}
											</>
										}
									/>
								</>
							}
						/>
					}
				/>
			) : (
				<></>
			)}

			<Modal
				visible={showDeactivateModal}
				onDismiss={handleCloseDeactivateModal}
				header="Confirm Deactivation"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button variant="link" onClick={handleCloseDeactivateModal}>
								Cancel
							</Button>
							<Button
								disabled={loadedDeactivated}
								variant="primary"
								onClick={handleConfirmDeactivate}
							>
								Deactivate
							</Button>
						</SpaceBetween>
					</Box>
				}
			>
				<Box>
					Are you sure you want to deactivate these promo codes? This action cannot be undone.
					<ul>
						{bonuses.selectedItems.map((item) => (
							<li key={item.id}>
								ID: {item.id}, Code: {item.code}
							</li>
						))}
					</ul>
					<Checkbox
						checked={deleteAllPromo}
						onChange={({ detail }) => setDeleteAllPromo(detail.checked)}
					>
						Delete all promo
					</Checkbox>
				</Box>
			</Modal>

			<Tabs
				activeTabId={activeTabId}
				onChange={handleTabChange}
				tabs={[
					{
						id: "promoCodes",
						label: "Promo Codes",
						content: (
							<Table
								stickyHeader="true"
								trackBy="id"
								loading={!bonuses.loaded}
								selectionType="multi"
								selectedItems={bonuses.selectedItems}
								onSelectionChange={(event) => {
									setBonuses({
										...bonuses,
										selectedItems: event.detail.selectedItems,
									});
								}}
								sortingColumn={{ sortingField: bonuses.sorting.sortingColumn.column }}
								sortingDescending={bonuses.sorting.isDescending}
								onSortingChange={(sorting) => {
									setBonuses({
										...bonuses,
										sorting: {
											...bonuses.sorting,
											sortingColumn: {
												order: !sorting.detail.isDescending ? "ASC" : "DESC",
												column: sorting.detail.sortingColumn.sortingField,
											},
											isDescending: sorting.detail.isDescending,
										},
									});

									setTimeout(() => {
										Interface.loadBonuses();
									}, 250);
								}}
								columnDefinitions={filteredColumnDefinitions.map((col) => ({
									...col,
									cell: (item) => col.cell(item, {
										setBonusesEditModal,
										bonusesEditModal,
										config,
										styles,
										handleUIDClick,
										getTypeColor,
										getTypeByCode,
										setBonusesAddModal,
										bonusesAddModal,
									}),
								}))}
								items={bonuses.data.filter((item) => {
									return item.filtered;
								})}
								pagination={
									<Pagination
										disabled={!bonuses.loaded}
										currentPageIndex={bonuses.pagination.current}
										pagesCount={bonuses.pagination.total}
										onChange={(page) => {
											setBonuses({
												...bonuses,
												pagination: { ...bonuses.pagination, current: page.detail.currentPageIndex },
											});
											setTimeout(() => Interface.loadBonuses(), 250);
										}}
									/>
								}
								preferences={
									<Preferences
										cancelLabel="Cancel"
										confirmLabel="Confirm"
										pageSizePreference={{
											title: "Logs per page",
											options: config.bonuses.preferences.perPage,
										}}
										visibleContentPreference={{
											title: "Select visible columns",
											options: [
												{
													label: "Columns",
													options: visibleContentOptions,
												},
											],
										}}
										preferences={{
											pageSize: config.bonuses.perPage,
											visibleContent: Object.keys(columnVisibility).filter(
												(id) => columnVisibility[id]
											),
										}}
										title="Preferences"
										onConfirm={(preferences) => {
											const newPageSize = preferences.detail.pageSize;
											const newVisibleContent = preferences.detail.visibleContent || [];

											const newColumnVisibility = {};
											initialColumnDefinitions.forEach((col) => {
												newColumnVisibility[col.id] = newVisibleContent.includes(col.id);
											});
											setColumnVisibility(newColumnVisibility);


											localStorage.setItem(
												"bonuses.columnVisibility",
												JSON.stringify(newColumnVisibility)
											);
											localStorage.setItem("bonuses.perPage", newPageSize);

											setConfig({
												...config,
												bonuses: { ...config.bonuses, perPage: newPageSize },
											});

											setTimeout(() => Interface.loadBonuses(), 250);
										}}
									/>
								}
								filter={
									<Box
										margin={{ bottom: "m" }}
										children={
											<Grid
												gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 3 }, { colspan: 3 }]}
												children={
													<>
														<Select
															filteringType="auto"
															disabled={!bonuses.loaded}
															placeholder="Type"
															selectedOption={bonuses.currentOption.type}
															onChange={(option) => {
																setBonuses({
																	...bonuses,
																	currentSearchType: option.detail.selectedOption.type,
																	currentOption: {
																		...bonuses.currentOption,
																		type: option.detail.selectedOption,
																	},
																});
															}}
															options={config.bonuses.main.options.type}
														/>
														<Input
															disabled={!bonuses.loaded}
															onKeyDown={handleKeyPress}
															disableBrowserAutocorrect={true}
															placeholder="UID, id, code or something"
															inputMode="search"
															type="search"
															value={bonuses.currentSearch}
															onChange={handleInputDebounceChange}
														/>
													</>
												}
											/>
										}
									/>
								}
								header={
									<Box
										margin={{ bottom: "m" }}
										children={
											<Header
												children="Promo codes"
												counter={<>({bonuses.pagination.count})</>}
												actions={
													<SpaceBetween direction="horizontal" size="l">
														<Button
															iconName="download"
															disabled={!bonuses.loaded || isExporting}
															loading={isExporting}
															className={[styles["players-search-button"], styles["btn"]].join(" ")}
															onClick={handleExportToCsv}
															variant="normal"
															children="Export to CSV"
														/>
														<Button
															iconName="edit"
															disabled={
																!bonuses.loaded ||
																bonuses.selectedItems.length > 1 ||
																bonuses.selectedItems.length === 0
															}
															className={[styles["players-search-button"], styles["btn"]].join(" ")}
															onClick={() => {
																const item = bonuses.selectedItems[0];
																setBonusesEditModal({
																	...bonusesEditModal,
																	id: item.id,
																	oldNote: item.note,
																	count: item.count,
																	value: item.value,
																	editAll: true,
																	advancedSettingsEnabled: item.reason ? true : false,
																	type: config.bonuses.options.type.filter(
																		(element) => element.type === item.type
																	)[0],
																	depositCountDays: typeof item.deposit_days === 'number' ? item.deposit_days : "",
																	depositCountTo: typeof item.deposit_count_to === 'number' ? item.deposit_count_to : "",
																	isPublic: item.type === 0,
																	bonusCode: item.bonus_code ? item.bonus_code : "",
																	wager: item.wager ? { value: item.wager, label: `x${item.wager}` } : null,
																	maxWithdrawal: item.max_withdrawal ? item.max_withdrawal / 100 : "",
																	depositTo: item.deposit_amount_to ? item.deposit_amount_to / 100 : "",
																	depositFrom: item.deposit_amount_from ? item.deposit_amount_from / 100 : "",
																	depositCount: typeof item.deposit_count === 'number' ? item.deposit_count : "",
																	emailChecked: item.require_email ? item.require_email : false,
																	phoneChecked: item.require_phone ? item.require_phone : false,
																	tgChecked: item.require_tg ? item.require_tg : false,
																	verifiedChecked: item.require_verification ? item.require_verification : false,
																	lastDepositDateFrom:
																		item.deposit_period_from &&
																		item.deposit_period_from !== "0000-00-00 00:00:00"
																			? item.deposit_period_from?.replace(" ", "T")
																			: null,
																	lastDepositDateTo:
																		item.deposit_period_to &&
																		item.deposit_period_to !== "0000-00-00 00:00:00"
																			? item.deposit_period_to?.replace(" ", "T")
																			: null,
																	otherPromoActivated: item.restrict_other_promo_amount ? true : false,
																	otherPromoAmount: item.restrict_other_promo_amount
																		? item.restrict_other_promo_amount / 100
																		: "",
																	otherPromoDateFrom:
																		item.restrict_other_promo_date_from &&
																		item.restrict_other_promo_date_from !== "0000-00-00 00:00:00"
																			? item.restrict_other_promo_date_from?.replace("_", "T")
																			: null,
																	otherPromoDateTo:
																		item.restrict_other_promo_date_to &&
																		item.restrict_other_promo_date_to !== "0000-00-00 00:00:00"
																			? item.restrict_other_promo_date_to?.replace("_", "T")
																			: null,
																	activation_lifetime_days:
																		typeof item.activationLifeTimeDays === 'number'
																			? item.activationLifeTimeDays
																			: typeof item.activation_lifetime_days === 'number'
																				? item.activation_lifetime_days
																				: item.activationLifeTimeDays || item.activation_lifetime_days || "",
																	considerBetAmount: item.restrict_bets_amount ? true : false,
																	betAmount: item.restrict_bets_amount ? item.restrict_bets_amount / 100 : "",
																	betDateFrom:
																		item.restrict_bets_date_from &&
																		item.restrict_bets_date_from !== "0000-00-00 00:00:00"
																			? item.restrict_bets_date_from?.replace("_", "T")
																			: null,
																	betDateTo:
																		item.restrict_bets_date_to &&
																		item.restrict_bets_date_to !== "0000-00-00 00:00:00"
																			? item.restrict_bets_date_to?.replace("_", "T")
																			: null,
																	vipLevelMode: item.vip_level_type
																		? { value: item.vip_level_type, label: item.vip_level_type }
																		: "",
																	vipLevel: item.vip_level_value ? item.vip_level_value : "",
																	promoDateFrom:
																		item.from &&
																		item.from !== "0000-00-00 00:00:00"
																			? item.from?.replace("_", "T")
																			: null,
																	promoDateTo:
																		item.to &&
																		item.to !== "0000-00-00 00:00:00"
																			? item.to?.replace("_", "T")
																			: null,
																	reason: item.reason ? { value: item.reason, label: item.reason } : "",
																	note: item.note,
																	bonusMode: typeof item.bonusMode === 'number' ? item.bonusMode : Number(item.bonusMode) || 0,
																	depositPercent: typeof item.depositPercent === 'number' ? item.depositPercent : item.depositPercent || "",
																	depositApplyLimit: typeof item.depositApplyLimit === 'number' ? item.depositApplyLimit : item.depositApplyLimit || "",
																	minDeposit: typeof item.minDeposit === 'number'
																		? item.minDeposit / 100
																		: item.minDeposit ? Number(item.minDeposit) / 100 : "",
																	maxDeposit: typeof item.maxDeposit === 'number'
																		? item.maxDeposit / 100
																		: item.maxDeposit ? Number(item.maxDeposit) / 100 : "",
																	maxBonusAmount: typeof item.max_bonus_amount === 'number' ? item.max_bonus_amount / 100 : item.max_bonus_amount || "",
																	bonusType: item.bonus_type
																		? { value: item.bonus_type, label: item.bonus_type.charAt(0).toUpperCase() + item.bonus_type.slice(1) }
																		: { value: 'all', label: 'All' },
																	bonusAmount: item.value,
																	name: item.code,
																	uid: item.uid,
																	...getAffiliateModalState(item),
																	show: true,
																});
															}}
															variant="normal"
															children="Edit all"
														/>
														<Button
															iconName="close"
															disabled={!bonuses.loaded || bonuses.selectedItems.length === 0}
															className={[styles["players-search-button"], styles["btn"]].join(" ")}
															onClick={handleOpenDeactivateModal}
															variant="normal"
															children="Deactivate"
														/>
														<Button
															iconName="add-plus"
															disabled={!bonuses.loaded}
															className={[styles["players-search-button"], styles["btn"]].join(" ")}
															onClick={() => {
																setTimeout(() => {
																	setBonusesAddModal({
																		...bonusesAddModal,
																		show: true,
																		isPublic: true,
																		advancedSettingsEnabled: false,
																		type: false,
																		count: "",
																		uid: "",
																		value: "",
																		note: "",
																		name: "",
																		reason: null,
																		reasonIdentifier: "",
																		bonusCode: "",
																		bonusAmount: "",
																		maxBonusAmount: "",
																		bonusType: { label: 'All', value: 'all' },
																		wager: null,
																		showWagerConfirmation: false,
																		maxWithdrawal: "",
																		depositTo: "",
																		depositFrom: "",
																		depositCount: "",
																		depositCountTo: '',
																		depositCountDays: '',
																		description: {},
																		emailChecked: false,
																		phoneChecked: false,
																		tgChecked: false,
																		verifiedChecked: false,
																		otherPromoActivated: false,
																		otherPromoAmount: "",
																		otherPromoDateFrom: null,
																		otherPromoDateTo: null,
																		otherPromoDateType: "",
																		activation_lifetime_days: "",
																		promoDateFrom: null,
																		promoDateTo: null,
																		promoDateType: "",
																		considerBetAmount: false,
																		betAmount: "",
																		betDateFrom: null,
																		betDateTo: null,
																		betDateType: "",
																		trafficType: "",
																		trafficSource: "",
																		adCostTotal: "",
																		adPayments: [],
																		validityPeriod: null,
																		vipLevelMode: null,
																		vipLevel: null,
																		lastDepositDateFrom: null,
																		lastDepositDateTo: null,
																		lastDepositDateType: "",
																		playerIds: "",
																		promoCountGeneration: 0,
																		errors: {},
																		checked: false,
																	});
																}, 0);
															}}
															variant="primary"
															children="Create code"
														/>
														<Button
															iconName="refresh"
															disabled={!bonuses.loaded}
															className={[styles["players-search-button"], styles["btn"]].join(" ")}
															onClick={() => {
																Interface.loadBonuses();
															}}
															variant="normal"
															children="Refresh"
														/>
													</SpaceBetween>
												}
											/>
										}
									/>
								}
								empty="No promocodes found with requested filter"
							/>
						),
					},
					{
						id: "statistics",
						label: "Statistics",
						content: (
							<Box>
								<Container
									header={
										<Header variant="h2" description="View statistics of promo codes over different time periods">
											Promo Codes Statistics
										</Header>
									}
								>
									<Box margin={{ bottom: "m" }}>
										<Grid gridDefinition={[{ colspan: 12 }]}>
											<Select
												selectedOption={{
													value: statisticsPeriod || "day",
													label:
														statisticsPeriod === "day"
															? "Day"
															: statisticsPeriod === "week"
																? "Week"
																: "Month",
												}}
												onChange={({ detail }) => {
													setStatisticsPeriod(detail.selectedOption.value);
												}
												}
												options={[
													{ value: "day", label: "Day" },
													{ value: "week", label: "Week" },
													{ value: "month", label: "Month" },
												]}
												placeholder="Select Period"
											/>

											<DatePicker
												value={statisticsStartDate}
												onChange={handleDateChange("statisticsStartDate")}
												placeholder="Start Date"
											/>

											<DatePicker
												value={statisticsEndDate}
												onChange={handleDateChange("statisticsEndDate")}
												placeholder="End Date"
											/>

											<Button onClick={() => loadStatistics()}>
												Submit
											</Button>
										</Grid>
									</Box>
									{statisticsError && (
										<Box margin={{ top: "s" }}>
											<Alert type="error" header="Error" children={statisticsError} />
										</Box>
									)}
									{statisticsLoading ? (
										<Box textAlign="center">
											<Spinner size="large" />
										</Box>
									) : (
										<Box>
											{statisticsData.length > 0 && (
												<Table
													columnDefinitions={[
														{
															id: "header",
															header: (
																<Box fontWeight="bold" padding={{ vertical: "xxs" }}>
																	{startDate?.replaceAll("/", ".").split(".").reverse().join(".")} -{" "}
																	{endDate?.replaceAll("/", ".").split(".").reverse().join(".")}
																</Box>
															),
															cell: (item) => item.header,
														},
														...dateColumns,
													]}
													items={[
														{
															width: 100,
															header: "Total Issued Promo Codes",
															...statisticsData.reduce((acc, item, index) => {
																acc[`value-${index}`] = item.totalIssued;
																return acc;
															}, {}),
														},
														{
															header: "Total Activated Promo Codes",
															...statisticsData.reduce((acc, item, index) => {
																acc[`value-${index}`] = item.totalActivated;
																return acc;
															}, {}),
														},
														{
															header: "Activation Percentage",
															...statisticsData.reduce((acc, item, index) => {
																acc[`value-${index}`] = `${item.activationPercentage}%`;
																return acc;
															}, {}),
														},
													]}
													renderAriaLive={() => (!bonuses.loaded ? "Loading statistics" : "")}
													empty="No statistics available"
												/>
											)}
										</Box>
									)}
								</Container>
							</Box>
						),
					},
				]}
			/>
		</>
	);
};

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const bonuses = useRecoilValue(bonusesStore);
		const setBonuses = useSetRecoilState(bonusesStore);
		const config = useRecoilValue(configStore);
		const setConfig = useSetRecoilState(configStore);
		const playerModal = useRecoilValue(playerModalStore);
		const setPlayerModal = useSetRecoilState(playerModalStore);
		const bonusesAddModal = useRecoilValue(bonusesAddModalStore);
		const setBonusesAddModal = useSetRecoilState(bonusesAddModalStore);
		const bonusesEditModal = useRecoilValue(bonusesEditModalStore);
		const setBonusesEditModal = useSetRecoilState(bonusesEditModalStore);

		return (
			<Component
				{...props}
				bonusesEditModal={bonusesEditModal}
				setBonusesEditModal={setBonusesEditModal}
				bonuses={bonuses}
				setBonuses={setBonuses}
				config={config}
				setConfig={setConfig}
				playerModal={playerModal}
				setPlayerModal={setPlayerModal}
				bonusesAddModal={bonusesAddModal}
				setBonusesAddModal={setBonusesAddModal}
			/>
		);
	};
}

export default withRecoil(Bonuses);